import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff, ShieldCheck } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/v1/auth/invite/${token}`)
      .then(res => { setInvite(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || "Invitación inválida"); setLoading(false); });
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error("Las contraseñas no coinciden"); return; }
    if (password.length < 4) { toast.error("Mínimo 4 caracteres"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/v1/auth/accept-invite/${token}`, { password });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAccepted(true);
      toast.success("Cuenta creada con éxito!");
      setTimeout(() => { window.location.href = "/dashboard"; }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aceptar invitación");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Invitación no válida</h2>
            <p className="text-gray-500">{error}</p>
            <Button className="mt-6" onClick={() => navigate("/login")} data-testid="go-to-login">Ir al login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Cuenta creada!</h2>
            <p className="text-gray-500">Redirigiendo al dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center px-4" data-testid="accept-invite-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center space-y-3">
          <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
          </div>
          <CardTitle className="text-xl">Crear tu cuenta</CardTitle>
          <p className="text-sm text-gray-500">
            Has sido invitado a unirte a <span className="font-bold text-gray-700">{invite?.restaurant_name}</span>
          </p>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-3 mb-6 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{invite?.name}</p>
              <p className="text-xs text-gray-500">{invite?.email}</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 capitalize">{invite?.role}</Badge>
          </div>

          <form onSubmit={handleAccept} className="space-y-4">
            <div>
              <Label>Contraseña *</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo 4 caracteres"
                  required
                  className="pr-10"
                  data-testid="invite-password"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmar contraseña *</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className="mt-1"
                data-testid="invite-confirm-password"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 font-bold" data-testid="accept-invite-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "CREAR MI CUENTA"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
